import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  City,
  Place,
  Line,
  Station,
  StationConnection,
  StationConnectionDetail,
  StationSummary,
  LineSummary,
  LineStation,
  StationFacility,
  StationAmenity,
  RouteRequest,
  RouteResponse,
  LoginRequest,
  LoginResponse,
  PlatformAuthRequest,
  ApiResponse,
  PaginatedResponse,
  MetroStats,
  LineRoute,
  Plan,
  UserPlan,
  CreatePlanRequest,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  AppVersion,
  Journey,
  PushNotificationRequest,
  PushNotificationResponse,
  NotificationRuleInfo,
  RoutePathRequest,
  RoutePathResponse,
  Incident,
  CreateIncidentRequest,
  UpdateIncidentStatusRequest,
  UpdateIncidentPriorityRequest,
  ResolveIncidentRequest,
  AddIncidentNotesRequest,
  AddIncidentMetadataRequest,
  IncidentStatistics,
  FareRule,
  FareRuleType,
  FareCalculationRequest,
  FareCalculationResponse,
  FareCondition,
  AppFeature
} from '../types';

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (credentials: LoginRequest): Promise<AxiosResponse<LoginResponse>> =>
    api.post('/auth/login', credentials),
  
  platformAuth: (credentials: PlatformAuthRequest): Promise<AxiosResponse<LoginResponse>> =>
    api.post('/auth/platform', credentials),
  
  validateToken: (): Promise<AxiosResponse<{ valid: boolean }>> =>
    api.get('/auth/validate'),
  
  changePassword: (request: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.put('/auth/change-password', request),
};

// City API
export const cityAPI = {
  getAll: (): Promise<AxiosResponse<City[]>> =>
    api.get('/cities'),
  
  getById: (id: string): Promise<AxiosResponse<City>> =>
    api.get(`/cities/${id}`),
  
  create: (city: Omit<City, 'id'>): Promise<AxiosResponse<City>> =>
    api.post('/cities', city),
  
  update: (id: string, city: Partial<City>): Promise<AxiosResponse<City>> =>
    api.put(`/cities/${id}`, city),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/cities/${id}`),
  
  getByName: (name: string): Promise<AxiosResponse<City>> =>
    api.get(`/cities/name/${name}`),
  
  getByNetworkId: (networkId: string): Promise<AxiosResponse<City>> =>
    api.get(`/cities/network/${networkId}`),
};

// Place API
export const placeAPI = {
  getAll: (): Promise<AxiosResponse<Place[]>> =>
    api.get('/places'),
  
  getById: (id: string): Promise<AxiosResponse<Place>> =>
    api.get(`/places/${id}`),
  
  create: (place: Omit<Place, 'id'>): Promise<AxiosResponse<Place>> =>
    api.post('/places', place),
  
  update: (id: string, place: Partial<Place>): Promise<AxiosResponse<Place>> =>
    api.put(`/places/${id}`, place),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/places/${id}`),
  
  getByCityId: (cityId: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}`),
  
  getActiveByCityId: (cityId: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/active`),
  
  getByCityIdAndType: (cityId: string, placeType: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/type/${placeType}`),
  
  getByCityIdAndCategory: (cityId: string, category: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/category/${category}`),
  
  searchByName: (name: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/search?name=${encodeURIComponent(name)}`),
  
  searchByNameInCity: (cityId: string, name: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/search/city/${cityId}?name=${encodeURIComponent(name)}`),
  
  findNearby: (latitude: number, longitude: number, radiusInKm: number): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/nearby?lat=${latitude}&lng=${longitude}&radius=${radiusInKm}`),
  
  findNearbyInCity: (cityId: string, latitude: number, longitude: number, radiusInKm: number): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/nearby/city/${cityId}?lat=${latitude}&lng=${longitude}&radius=${radiusInKm}`),
  
  getByType: (placeType: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/type/${placeType}`),
  
  getByCategory: (category: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/category/${category}`),
  
  getWheelchairAccessibleByCityId: (cityId: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/accessible`),
  
  getWithParkingByCityId: (cityId: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/parking`),
  
  getWithPublicTransportByCityId: (cityId: string): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/public-transport`),
  
  getMostVisitedByCityId: (cityId: string, limit: number = 10): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/popular/visited?limit=${limit}`),
  
  getMostSearchedByCityId: (cityId: string, limit: number = 10): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/popular/searched?limit=${limit}`),
  
  getHighestRatedByCityId: (cityId: string, limit: number = 10): Promise<AxiosResponse<Place[]>> =>
    api.get(`/places/city/${cityId}/popular/rated?limit=${limit}`),
  
  recordVisit: (id: string): Promise<AxiosResponse<void>> =>
    api.post(`/places/${id}/visit`),
  
  recordSearch: (id: string): Promise<AxiosResponse<void>> =>
    api.post(`/places/${id}/search`),
  
  updateRating: (id: string, rating: number): Promise<AxiosResponse<void>> =>
    api.put(`/places/${id}/rating?rating=${rating}`),
  
  getCountByCityId: (cityId: string): Promise<AxiosResponse<number>> =>
    api.get(`/places/city/${cityId}/count`),
  
  getActiveCountByCityId: (cityId: string): Promise<AxiosResponse<number>> =>
    api.get(`/places/city/${cityId}/count/active`),
  
  setActiveStatus: (id: string, isActive: boolean): Promise<AxiosResponse<void>> =>
    api.put(`/places/${id}/status?isActive=${isActive}`),
};

// Line API
export const lineAPI = {
  getAll: (): Promise<AxiosResponse<Line[]>> =>
    api.get('/lines'),
  
  getById: (id: string): Promise<AxiosResponse<Line>> =>
    api.get(`/lines/${id}`),
  
  create: (line: Omit<Line, 'id'>): Promise<AxiosResponse<Line>> =>
    api.post('/lines', line),
  
  update: (id: string, line: Partial<Line>): Promise<AxiosResponse<Line>> =>
    api.put(`/lines/${id}`, line),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/lines/${id}`),
  
  getByName: (name: string): Promise<AxiosResponse<Line>> =>
    api.get(`/lines/name/${name}`),
  
  getByCity: (cityId: string): Promise<AxiosResponse<Line[]>> =>
    api.get(`/lines/city/${cityId}`),
    
  generatePath: (id: string): Promise<AxiosResponse<{status: string; message: string; data: any}>> =>
    api.post(`/lines/${id}/generate-path`),
};

// Station API
export const stationAPI = {
  getAll: (): Promise<AxiosResponse<Station[]>> =>
    api.get('/stations'),
  
  getById: (id: string): Promise<AxiosResponse<Station>> =>
    api.get(`/stations/${id}`),
  
  create: (station: Omit<Station, 'id'>): Promise<AxiosResponse<Station>> =>
    api.post('/stations', station),
  
  update: (id: string, station: Partial<Station>): Promise<AxiosResponse<Station>> =>
    api.put(`/stations/${id}`, station),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/stations/${id}`),
  
  getByName: (name: string): Promise<AxiosResponse<Station>> =>
    api.get(`/stations/name/${name}`),
  
  getByCity: (cityId: string): Promise<AxiosResponse<Station[]>> =>
    api.get(`/stations/city/${cityId}`),
  
  getInterchangeStations: (): Promise<AxiosResponse<Station[]>> =>
    api.get('/stations/interchange'),
  
  filterStations: (filter: {
    cityId?: string;
    cityCode?: string;
    stationName?: string;
    stationCode?: string;
    isInterchange?: boolean;
    isActive?: boolean;
    status?: string;
  }): Promise<AxiosResponse<Station[]>> =>
    api.post('/stations/filter', filter),
    
  searchStations: (filter: {
    name?: string;
    code?: string;
    city?: string;
  }, page: number = 0, size: number = 20): Promise<AxiosResponse<{
    content: Station[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
  }>> =>
    api.post(`/stations/search?page=${page}&size=${size}`, filter),
  
  // Import functionality
  getFileHeaders: (file: File): Promise<AxiosResponse<string[]>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/stations/import/headers', formData);
  },
  
  importStations: (file: File, importRequest: {
    fieldMapping: Record<string, string>;
    updateExisting?: boolean;
    skipErrors?: boolean;
  }): Promise<AxiosResponse<{
    totalRows: number;
    created: number;
    updated: number;
    errors: number;
    errorMessages: string[];
    status: string;
    processingTime: number;
    message: string;
  }>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fieldMapping', JSON.stringify(importRequest.fieldMapping));
    formData.append('updateExisting', (importRequest.updateExisting ?? true).toString());
    formData.append('skipErrors', (importRequest.skipErrors ?? false).toString());
    
    return api.post('/stations/import', formData);
  },
};

// Station Connection API
export const stationConnectionAPI = {
  getAll: (): Promise<AxiosResponse<StationConnectionDetail[]>> =>
    api.get('/station-connections'),
  
  getById: (id: string): Promise<AxiosResponse<StationConnectionDetail>> =>
    api.get(`/station-connections/${id}`),
  
  create: (connection: Omit<StationConnection, 'id'>): Promise<AxiosResponse<StationConnection>> =>
    api.post('/station-connections', connection),
  
  update: (id: string, connection: Partial<StationConnection>): Promise<AxiosResponse<StationConnection>> =>
    api.put(`/station-connections/${id}`, connection),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/station-connections/${id}`),
  
  getByFromStation: (stationId: string): Promise<AxiosResponse<StationConnectionDetail[]>> =>
    api.get(`/station-connections/from/${stationId}`),
  
  getByToStation: (stationId: string): Promise<AxiosResponse<StationConnectionDetail[]>> =>
    api.get(`/station-connections/to/${stationId}`),
  
  getByLine: (lineId: string): Promise<AxiosResponse<StationConnectionDetail[]>> =>
    api.get(`/station-connections/line/${lineId}`),
  
  getBetweenStations: (fromStationId: string, toStationId: string): Promise<AxiosResponse<StationConnectionDetail[]>> =>
    api.get(`/station-connections/between/${fromStationId}/${toStationId}`),
  
  getInterchange: (): Promise<AxiosResponse<StationConnectionDetail[]>> =>
    api.get('/station-connections/interchange'),
  
  getBulkDistances: (stationPairs: Array<{fromStationId: string, toStationId: string, fromStationName: string, toStationName: string}>): Promise<AxiosResponse<any[]>> =>
    api.post('/station-connections/bulk-distances', stationPairs),
};

// Line Station API
export const lineStationAPI = {
  getAll: (): Promise<AxiosResponse<LineStation[]>> =>
    api.get('/line-stations'),
  
  getById: (id: string): Promise<AxiosResponse<LineStation>> =>
    api.get(`/line-stations/${id}`),
  
  create: (lineStation: Omit<LineStation, 'id'>): Promise<AxiosResponse<LineStation>> =>
    api.post('/line-stations', lineStation),
  
  update: (id: string, lineStation: Partial<LineStation>): Promise<AxiosResponse<LineStation>> =>
    api.put(`/line-stations/${id}`, lineStation),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/line-stations/${id}`),
  
  getByLine: (lineId: string): Promise<AxiosResponse<LineStation[]>> =>
    api.get(`/line-stations/line/${lineId}`),
  
  getByStation: (stationId: string): Promise<AxiosResponse<LineStation[]>> =>
    api.get(`/line-stations/station/${stationId}`),
  
  getInterchangeStations: (): Promise<AxiosResponse<LineStation[]>> =>
    api.get('/line-stations/interchange'),
};

// Station Facility API
export const stationFacilityAPI = {
  getAll: (): Promise<AxiosResponse<StationFacility[]>> =>
    api.get('/station-facilities'),
  
  getById: (id: string): Promise<AxiosResponse<StationFacility>> =>
    api.get(`/station-facilities/${id}`),
  
  create: (facility: Omit<StationFacility, 'id'>): Promise<AxiosResponse<StationFacility>> =>
    api.post('/station-facilities', facility),
  
  update: (id: string, facility: Partial<StationFacility>): Promise<AxiosResponse<StationFacility>> =>
    api.put(`/station-facilities/${id}`, facility),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/station-facilities/${id}`),
  
  getByStation: (stationId: string): Promise<AxiosResponse<StationFacility[]>> =>
    api.get(`/station-facilities/station/${stationId}`),
  
  getByType: (facilityType: string): Promise<AxiosResponse<StationFacility[]>> =>
    api.get(`/station-facilities/type/${facilityType}`),
};

// Station Amenity API
export const stationAmenityAPI = {
  getAll: (): Promise<AxiosResponse<StationAmenity[]>> =>
    api.get('/station-amenities'),
  
  getById: (id: string): Promise<AxiosResponse<StationAmenity>> =>
    api.get(`/station-amenities/${id}`),
  
  create: (amenity: Omit<StationAmenity, 'id'>): Promise<AxiosResponse<StationAmenity>> =>
    api.post('/station-amenities', amenity),
  
  update: (id: string, amenity: Partial<StationAmenity>): Promise<AxiosResponse<StationAmenity>> =>
    api.put(`/station-amenities/${id}`, amenity),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/station-amenities/${id}`),
  
  getByStation: (stationId: string): Promise<AxiosResponse<StationAmenity[]>> =>
    api.get(`/station-amenities/station/${stationId}`),
  
  getByType: (amenityType: string): Promise<AxiosResponse<StationAmenity[]>> =>
    api.get(`/station-amenities/type/${amenityType}`),
};

// Route Planning API
export const routeAPI = {
  planRoute: (request: RouteRequest): Promise<AxiosResponse<RouteResponse>> =>
    api.post('/routes/plan', request),
  
  planAllRoutes: (request: RouteRequest): Promise<AxiosResponse<RouteResponse[]>> =>
    api.post('/routes/plan/all', request),
  
  findDirectRoute: (sourceStation: string, destinationStation: string): Promise<AxiosResponse<RouteResponse>> =>
    api.get(`/routes/direct/${sourceStation}/${destinationStation}`),
  
  findRoutesWithMinInterchanges: (sourceStation: string, destinationStation: string): Promise<AxiosResponse<RouteResponse[]>> =>
    api.get(`/routes/min-interchanges/${sourceStation}/${destinationStation}`),
  
  findRoutesWithMinDistance: (sourceStation: string, destinationStation: string): Promise<AxiosResponse<RouteResponse[]>> =>
    api.get(`/routes/min-distance/${sourceStation}/${destinationStation}`),
  
  findRoutesWithMinTime: (sourceStation: string, destinationStation: string): Promise<AxiosResponse<RouteResponse[]>> =>
    api.get(`/routes/min-time/${sourceStation}/${destinationStation}`),
  
  getRouteAlternatives: (sourceStation: string, destinationStation: string, criteria: string): Promise<AxiosResponse<RouteResponse[]>> =>
    api.get(`/routes/alternatives/${sourceStation}/${destinationStation}?criteria=${criteria}`),
  
  // Quick route planning endpoint
  quickRoute: (sourceStation: string, destinationStation: string, criteria: string = 'SHORTEST_TIME'): Promise<AxiosResponse<RouteResponse>> =>
    api.get(`/routes/quick/${sourceStation}/${destinationStation}?criteria=${criteria}`),
};

// Line Route API
export const lineRouteAPI = {
  getAll: (): Promise<AxiosResponse<LineRoute[]>> =>
    api.get('/line-routes'),
  
  getById: (id: string): Promise<AxiosResponse<LineRoute>> =>
    api.get(`/line-routes/${id}`),
  
  create: (lineRoute: Omit<LineRoute, 'id'>): Promise<AxiosResponse<LineRoute>> =>
    api.post('/line-routes', lineRoute),
  
  update: (id: string, lineRoute: Partial<LineRoute>): Promise<AxiosResponse<LineRoute>> =>
    api.put(`/line-routes/${id}`, lineRoute),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/line-routes/${id}`),
  
  getByLine: (lineId: string): Promise<AxiosResponse<LineRoute[]>> =>
    api.get(`/line-routes/line/${lineId}`),
  
  getByStation: (stationId: string): Promise<AxiosResponse<LineRoute[]>> =>
    api.get(`/line-routes/station/${stationId}`),
};

// Platform API (for bulk operations)
export const platformAPI = {
  getHealth: (): Promise<AxiosResponse<{ status: string }>> =>
    api.get('/platform/health'),
  
  bulkCreateCities: (cities: Omit<City, 'id'>[]): Promise<AxiosResponse<City[]>> =>
    api.post('/platform/cities/bulk', cities),
  
  bulkCreateLines: (lines: Omit<Line, 'id'>[]): Promise<AxiosResponse<Line[]>> =>
    api.post('/platform/lines/bulk', lines),
  
  bulkCreateStations: (stations: Omit<Station, 'id'>[]): Promise<AxiosResponse<Station[]>> =>
    api.post('/platform/stations/bulk', stations),
  
  bulkCreateConnections: (connections: Omit<StationConnection, 'id'>[]): Promise<AxiosResponse<StationConnection[]>> =>
    api.post('/platform/connections/bulk', connections),
};

// Analytics API
export const analyticsAPI = {
  getMetroStats: (): Promise<AxiosResponse<MetroStats>> =>
    api.get('/analytics/stats'),
  
  getStationStats: (): Promise<AxiosResponse<any[]>> =>
    api.get('/analytics/stations'),
  
  getLineStats: (): Promise<AxiosResponse<any[]>> =>
    api.get('/analytics/lines'),
};

// API Logs API
export const apiLogsAPI = {
  getLatestLogs: (): Promise<AxiosResponse<any[]>> =>
    api.get('/api-logs'),
  
  getLogsWithPagination: (page: number, size: number): Promise<AxiosResponse<any>> =>
    api.get(`/api-logs/page?page=${page}&size=${size}`),
  
  getLogsByDateRange: (startDate: string, endDate: string): Promise<AxiosResponse<any[]>> =>
    api.get(`/api-logs/date-range?startDate=${startDate}&endDate=${endDate}`),
  
  getLogsByMethod: (method: string): Promise<AxiosResponse<any[]>> =>
    api.get(`/api-logs/method/${method}`),
  
  getLogsByEndpoint: (endpoint: string): Promise<AxiosResponse<any[]>> =>
    api.get(`/api-logs/endpoint/${endpoint}`),
  
  getLogsByStatusCode: (statusCode: number): Promise<AxiosResponse<any[]>> =>
    api.get(`/api-logs/status/${statusCode}`),
  
  getLogsByUserId: (userId: string): Promise<AxiosResponse<any[]>> =>
    api.get(`/api-logs/user/${userId}`),
  
  getErrorLogs: (): Promise<AxiosResponse<any[]>> =>
    api.get('/api-logs/errors'),
  
  getSlowRequests: (thresholdMs: number): Promise<AxiosResponse<any[]>> =>
    api.get(`/api-logs/slow?thresholdMs=${thresholdMs}`),
  
  getStatistics: (): Promise<AxiosResponse<any>> =>
    api.get('/api-logs/statistics'),
  
  getEndpointStatistics: (): Promise<AxiosResponse<any>> =>
    api.get('/api-logs/statistics/endpoints'),
  
  deleteLogsOlderThan: (date: string): Promise<AxiosResponse<any>> =>
    api.delete(`/api-logs/older-than?date=${date}`),
  
  clearAllLogs: (): Promise<AxiosResponse<any>> =>
    api.delete('/api-logs/all'),
};

// Translation API
export const translationAPI = {
  translateAndSave: (request: {
    entityType: string;
    entityId: string;
    fieldName: string;
    sourceText: string;
    targetLanguage: string;
  }): Promise<AxiosResponse<any>> =>
    api.post('/translations/translate', request),
  
  bulkTranslateAndSave: (entityType: string, entityId: string, targetLanguage: string, fields: Record<string, string>): Promise<AxiosResponse<any>> =>
    api.post(`/translations/translate/bulk?entityType=${entityType}&entityId=${entityId}&targetLanguage=${targetLanguage}`, fields),
  
  getSupportedLanguages: (): Promise<AxiosResponse<any>> =>
    api.get('/translations/supported-languages'),
  
  getTranslationStats: (): Promise<AxiosResponse<any>> =>
    api.get('/translations/stats'),
  
  getPendingVerifications: (): Promise<AxiosResponse<any>> =>
    api.get('/translations/pending-verifications'),
  
  verifyTranslation: (translationId: string, correctedText: string): Promise<AxiosResponse<any>> =>
    api.put(`/translations/verify/${translationId}?correctedText=${encodeURIComponent(correctedText)}`),
  
  addManualTranslation: (request: {
    entityType: string;
    entityId: string;
    fieldName: string;
    sourceText: string;
    targetLanguage: string;
    translatedText: string;
  }): Promise<AxiosResponse<any>> =>
    api.post('/translations/manual', null, { params: request }),
};

// Plan Management API
export const planAPI = {
  createPlan: (plan: CreatePlanRequest): Promise<AxiosResponse<Plan>> =>
    api.post('/plans', plan),
  
  getAllPlans: (): Promise<AxiosResponse<Plan[]>> =>
    api.get('/plans'),
  
  getPlanById: (id: string): Promise<AxiosResponse<Plan>> =>
    api.get(`/plans/${id}`),
  
  getPlanByPlanId: (planId: string): Promise<AxiosResponse<Plan>> =>
    api.get(`/plans/plan-id/${planId}`),
  
  getActivePlans: (): Promise<AxiosResponse<Plan[]>> =>
    api.get('/plans/active'),
  
  getPopularPlans: (): Promise<AxiosResponse<Plan[]>> =>
    api.get('/plans/popular'),
  
  updatePlan: (id: string, plan: CreatePlanRequest): Promise<AxiosResponse<Plan>> =>
    api.put(`/plans/${id}`, plan),
  
  deletePlan: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/plans/${id}`),
  
  activatePlan: (id: string): Promise<AxiosResponse<Plan>> =>
    api.put(`/plans/${id}/activate`),
  
  deactivatePlan: (id: string): Promise<AxiosResponse<Plan>> =>
    api.put(`/plans/${id}/deactivate`),
};

// User Plan Management API
export const userPlanAPI = {
  createUserPlan: (userPlan: {
    userId: string;
    planId: string;
    paymentMethod: string;
    amount: number;
    platform: string;
    transactionId?: string;
  }): Promise<AxiosResponse<UserPlan>> =>
    api.post('/user-plans', null, { params: userPlan }),
  
  getUserPlanById: (id: string): Promise<AxiosResponse<UserPlan>> =>
    api.get(`/user-plans/${id}`),
  
  getUserPlansByUserId: (userId: string): Promise<AxiosResponse<UserPlan[]>> =>
    api.get(`/user-plans/user/${userId}`),
  
  getActiveUserPlan: (userId: string): Promise<AxiosResponse<UserPlan>> =>
    api.get(`/user-plans/user/${userId}/active`),
  
  getUserPlanByUserIdAndPlanId: (userId: string, planId: string): Promise<AxiosResponse<UserPlan>> =>
    api.get(`/user-plans/user/${userId}/plan/${planId}`),
  
  updateUserPlanStatus: (id: string, status: string): Promise<AxiosResponse<UserPlan>> =>
    api.put(`/user-plans/${id}/status`, null, { params: { status } }),
  
  cancelUserPlan: (id: string): Promise<AxiosResponse<UserPlan>> =>
    api.put(`/user-plans/${id}/cancel`),
  
  renewUserPlan: (id: string, newEndDate: string): Promise<AxiosResponse<UserPlan>> =>
    api.put(`/user-plans/${id}/renew`, null, { params: { newEndDate } }),
  
  getExpiredUserPlans: (): Promise<AxiosResponse<UserPlan[]>> =>
    api.get('/user-plans/expired'),
  
  getAllUserPlans: (): Promise<AxiosResponse<UserPlan[]>> =>
    api.get('/user-plans'),
  
  deleteUserPlan: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/user-plans/${id}`),
};

// User Management API
export const userAPI = {
  getAllUsers: (): Promise<AxiosResponse<User[]>> =>
    api.get('/users'),
  
  getUserById: (id: string): Promise<AxiosResponse<User>> =>
    api.get(`/users/${id}`),
  
  createUser: (user: CreateUserRequest): Promise<AxiosResponse<User>> =>
    api.post('/users', user),
  
  updateUser: (id: string, user: UpdateUserRequest): Promise<AxiosResponse<User>> =>
    api.put(`/users/${id}`, user),
  
  deleteUser: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/users/${id}`),
  
  activateUser: (id: string): Promise<AxiosResponse<User>> =>
    api.put(`/users/${id}/activate`),
  
  deactivateUser: (id: string): Promise<AxiosResponse<User>> =>
    api.put(`/users/${id}/deactivate`),
  
  getUserByUsername: (username: string): Promise<AxiosResponse<User>> =>
    api.get(`/users/username/${username}`),
  
  getUserByEmail: (email: string): Promise<AxiosResponse<User>> =>
    api.get(`/users/email/${email}`),
  
  getUsersByCity: (cityId: string): Promise<AxiosResponse<User[]>> =>
    api.get(`/users/city/${cityId}`),
  
  getActiveUsers: (): Promise<AxiosResponse<User[]>> =>
    api.get('/users/active'),
  
  getInactiveUsers: (): Promise<AxiosResponse<User[]>> =>
    api.get('/users/inactive'),
};

// App Version Management API
export const appVersionAPI = {
  getAll: (): Promise<AxiosResponse<AppVersion[]>> =>
    api.get('/app/version'),
  
  getById: (id: string): Promise<AxiosResponse<AppVersion>> =>
    api.get(`/app/version/${id}`),
  
  create: (appVersion: Omit<AppVersion, 'id'>): Promise<AxiosResponse<AppVersion>> =>
    api.post('/app/version', appVersion),
  
  update: (id: string, appVersion: Omit<AppVersion, 'id'>): Promise<AxiosResponse<AppVersion>> =>
    api.put(`/app/version/${id}`, appVersion),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/app/version/${id}`),
  
  getActiveByPlatform: (platform: string): Promise<AxiosResponse<AppVersion>> =>
    api.get(`/app/version/platform/${platform}`),
  
  activate: (id: string): Promise<AxiosResponse<AppVersion>> =>
    api.put(`/app/version/${id}/activate`),
  
  deactivate: (id: string): Promise<AxiosResponse<AppVersion>> =>
    api.put(`/app/version/${id}/deactivate`),
  
  checkVersion: (platform: string, currentVersion?: string): Promise<AxiosResponse<AppVersion>> =>
    api.get('/app/version/check', { params: { platform, currentVersion } }),
};

// Journey Management API
export const journeyAPI = {
  getAll: (): Promise<AxiosResponse<Journey[]>> =>
    api.get('/journeys'),
  
  getById: (id: string): Promise<AxiosResponse<Journey>> =>
    api.get(`/journeys/${id}`),
  
  getActiveJourney: (userId: string): Promise<AxiosResponse<Journey>> =>
    api.get(`/journeys/active`),
  
  getUserJourneys: (userId: string, page?: number, size?: number): Promise<AxiosResponse<PaginatedResponse<Journey>>> =>
    api.get(`/journeys`, { params: { page, size } }),
  
  getJourneysByStatus: (userId: string, status: string): Promise<AxiosResponse<Journey[]>> =>
    api.get(`/journeys/status/${status}`),
  
  getJourneysByDateRange: (userId: string, startDate: string, endDate: string): Promise<AxiosResponse<Journey[]>> =>
    api.get('/journeys/date-range', { params: { startDate, endDate } }),
  
  startJourney: (journeyData: {
    userId: string;
    journeyName?: string;
    sourceStationId: string;
    sourceStationName: string;
    sourceStationCode: string;
    sourceStationLineId: string;
    sourceStationLineName: string;
    sourceStationLineColor: string;
    sourceStationLatitude: number;
    sourceStationLongitude: number;
    destinationStationId?: string;
    destinationStationName?: string;
    destinationStationCode?: string;
    destinationStationLineId?: string;
    destinationStationLineName?: string;
    destinationStationLineColor?: string;
    destinationStationLatitude?: number;
    destinationStationLongitude?: number;
    intermediateStations?: any[];
  }): Promise<AxiosResponse<Journey>> =>
    api.post('/journeys/start', journeyData),
  
  updateLocation: (journeyId: string, eventId: string, lat: number, lng: number, accuracy?: number, speed?: number, timestamp?: number): Promise<AxiosResponse<Journey>> =>
    api.put('/journeys/location', { journeyId, eventId, lat, lng, accuracy, speed, timestamp }),
  
  completeJourney: (id: string): Promise<AxiosResponse<Journey>> =>
    api.put(`/journeys/${id}/complete`),
  
  stopJourney: (id: string): Promise<AxiosResponse<Journey>> =>
    api.put(`/journeys/${id}/stop`),
  
  shareJourney: (journeyId: string, shareData: any): Promise<AxiosResponse<string>> =>
    api.post('/journeys/share', { journeyId, ...shareData }),
  
  cleanupOldJourneys: (): Promise<AxiosResponse<number>> =>
    api.post('/journeys/cleanup'),
};

// Push Notification API
export const pushNotificationAPI = {
  sendNotification: (request: PushNotificationRequest): Promise<AxiosResponse<PushNotificationResponse>> =>
    api.post('/push-notifications/send', request),
  
  sendToUsers: (userIds: string[], title: string, message: string, actionUrl?: string): Promise<AxiosResponse<PushNotificationResponse>> =>
    api.post('/push-notifications/send/users', null, {
      params: { userIds, title, message, actionUrl }
    }),
  
  sendToAllUsers: (title: string, message: string, actionUrl?: string): Promise<AxiosResponse<PushNotificationResponse>> =>
    api.post('/push-notifications/send/all', null, {
      params: { title, message, actionUrl }
    }),
  
  sendToCityUsers: (cityIds: string[], title: string, message: string, actionUrl?: string): Promise<AxiosResponse<PushNotificationResponse>> =>
    api.post('/push-notifications/send/cities', null, {
      params: { cityIds, title, message, actionUrl }
    }),
  
  sendToPlatformUsers: (platform: string, title: string, message: string, actionUrl?: string): Promise<AxiosResponse<PushNotificationResponse>> =>
    api.post('/push-notifications/send/platform', null, {
      params: { platform, title, message, actionUrl }
    }),
  
  getAvailableRules: (): Promise<AxiosResponse<NotificationRuleInfo[]>> =>
    api.get('/push-notifications/rules'),
  
  validateRequest: (request: PushNotificationRequest): Promise<AxiosResponse<boolean>> =>
    api.post('/push-notifications/validate', request),
  
  getDeliveryStatus: (notificationId: string): Promise<AxiosResponse<PushNotificationResponse>> =>
    api.get(`/push-notifications/status/${notificationId}`),
};

// Route Path API
export const routePathAPI = {
  findRoutePath: (request: RoutePathRequest): Promise<AxiosResponse<RoutePathResponse>> =>
    api.post('/route-paths/find', request),
  
  getRoutePath: (sourceStationId: string, destinationStationId: string): Promise<AxiosResponse<RoutePathResponse>> =>
    api.get(`/route-paths/${sourceStationId}/${destinationStationId}`),
  
  getBulkRoutePaths: (connections: Array<{fromStationId: string, toStationId: string}>): Promise<AxiosResponse<any[]>> =>
    api.post('/route-paths/bulk', { connections }),
  
  createRoutePath: (request: RoutePathRequest): Promise<AxiosResponse<RoutePathResponse>> =>
    api.post('/route-paths/create', request),
  
  deleteRoutePath: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/route-paths/${id}`),
  
  regenerateRoutePath: (id: string): Promise<AxiosResponse<RoutePathResponse>> =>
    api.post(`/route-paths/${id}/regenerate`),
  
  regenerateRoutePathByStations: (request: RoutePathRequest): Promise<AxiosResponse<RoutePathResponse>> =>
    api.post('/route-paths/regenerate', request),
  
  generateAllRoutePaths: (): Promise<AxiosResponse<{ generated: number; total: number; errors: string[]; message: string }>> =>
    api.post('/route-paths/generate-all'),
  
  generateRoutePathsForLine: (lineId: string): Promise<AxiosResponse<{ generated: number; total: number; errors: string[]; message: string }>> =>
    api.post(`/route-paths/generate-for-line/${lineId}`),
};

// Incident API
export const incidentAPI = {
  getAll: (): Promise<AxiosResponse<Incident[]>> =>
    api.get('/incidents'),
  
  getById: (id: string): Promise<AxiosResponse<Incident>> =>
    api.get(`/incidents/${id}`),
  
  create: (incident: CreateIncidentRequest): Promise<AxiosResponse<Incident>> =>
    api.post('/incidents', incident),
  
  updateStatus: (id: string, request: UpdateIncidentStatusRequest): Promise<AxiosResponse<Incident>> =>
    api.put(`/incidents/${id}/status`, request),
  
  updatePriority: (id: string, request: UpdateIncidentPriorityRequest): Promise<AxiosResponse<Incident>> =>
    api.put(`/incidents/${id}/priority`, request),
  
  resolve: (id: string, request: ResolveIncidentRequest): Promise<AxiosResponse<Incident>> =>
    api.put(`/incidents/${id}/resolve`, request),
  
  addNotes: (id: string, request: AddIncidentNotesRequest): Promise<AxiosResponse<Incident>> =>
    api.put(`/incidents/${id}/notes`, request),
  
  addMetadata: (id: string, request: AddIncidentMetadataRequest): Promise<AxiosResponse<Incident>> =>
    api.put(`/incidents/${id}/metadata`, request),
  
  updateMetadata: (id: string, metadata: Record<string, any>): Promise<AxiosResponse<Incident>> =>
    api.put(`/incidents/${id}/metadata/update`, metadata),
  
  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/incidents/${id}`),
  
  getByStatus: (status: string): Promise<AxiosResponse<Incident[]>> =>
    api.get(`/incidents/status/${status}`),
  
  getByPriority: (priority: string): Promise<AxiosResponse<Incident[]>> =>
    api.get(`/incidents/priority/${priority}`),
  
  getByIssueType: (issueType: string): Promise<AxiosResponse<Incident[]>> =>
    api.get(`/incidents/issue-type/${issueType}`),
  
  getByUserEmail: (userEmail: string): Promise<AxiosResponse<Incident[]>> =>
    api.get(`/incidents/user-email/${userEmail}`),
  
  getByUserId: (userId: string): Promise<AxiosResponse<Incident[]>> =>
    api.get(`/incidents/user-id/${userId}`),
  
  getByPlatform: (platform: string): Promise<AxiosResponse<Incident[]>> =>
    api.get(`/incidents/platform/${platform}`),
  
  getStatistics: (): Promise<AxiosResponse<IncidentStatistics>> =>
    api.get('/incidents/statistics'),
  
  search: (params: {
    issueType?: string;
    status?: string;
    priority?: string;
    platform?: string;
    cityId?: string;
    userEmail?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<Incident[]>> =>
    api.get('/incidents/search', { params }),
};

// Cache API
export interface CacheStats {
  totalCaches: number;
  caches: Record<string, {
    name: string;
    available: boolean;
    nativeCacheType?: string;
  }>;
}

export interface CacheClearResponse {
  message: string;
  status: string;
}

export interface CacheClearAllResponse {
  message: string;
  totalCaches: number;
  clearedCount: number;
  clearedCaches: Record<string, string>;
}

export const cacheAPI = {
  getCacheNames: (): Promise<AxiosResponse<string[]>> =>
    api.get('/cache/names'),
  
  clearCache: (cacheName: string): Promise<AxiosResponse<CacheClearResponse>> =>
    api.delete(`/cache/${cacheName}`),
  
  clearAllCaches: (): Promise<AxiosResponse<CacheClearAllResponse>> =>
    api.delete('/cache/all'),
  
  getCacheStats: (): Promise<AxiosResponse<CacheStats>> =>
    api.get('/cache/stats'),
};

// Fare API
export const fareAPI = {
  // Calculate fare
  calculateFare: (request: FareCalculationRequest): Promise<AxiosResponse<FareCalculationResponse>> =>
    api.post('/fares/calculate', request),
  
  // City fare rules
  createCityFareRule: (rule: Partial<FareRule>): Promise<AxiosResponse<FareRule>> =>
    api.post('/fares/rules/city', rule),
  
  // Line fare rules
  createLineFareRule: (rule: Partial<FareRule>): Promise<AxiosResponse<FareRule>> =>
    api.post('/fares/rules/line', rule),
  
  // Station fare rules
  createStationFareRule: (rule: Partial<FareRule>): Promise<AxiosResponse<FareRule>> =>
    api.post('/fares/rules/station', rule),
  
  // Get fare rule by ID
  getFareRuleById: (id: string): Promise<AxiosResponse<FareRule>> =>
    api.get(`/fares/rules/${id}`),
  
  // Update fare rule
  updateFareRule: (id: string, rule: Partial<FareRule>): Promise<AxiosResponse<FareRule>> =>
    api.put(`/fares/rules/${id}`, rule),
  
  // Delete fare rule
  deleteFareRule: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/fares/rules/${id}`),
  
  // Get fare rules by city
  getFareRulesByCity: (cityId: string): Promise<AxiosResponse<FareRule[]>> =>
    api.get(`/fares/rules/city/${cityId}`),
  
  // Get fare rules by city and type
  getFareRulesByCityAndType: (cityId: string, ruleType: FareRuleType): Promise<AxiosResponse<FareRule[]>> =>
    api.get(`/fares/rules/city/${cityId}/type/${ruleType}`),
  
  // Activate fare rule
  activateFareRule: (id: string): Promise<AxiosResponse<void>> =>
    api.put(`/fares/rules/${id}/activate`),
  
  // Deactivate fare rule
  deactivateFareRule: (id: string): Promise<AxiosResponse<void>> =>
    api.put(`/fares/rules/${id}/deactivate`),
  
  // Fare conditions
  // Create fare condition
  createCondition: (condition: Partial<FareCondition>): Promise<AxiosResponse<FareCondition>> =>
    api.post('/fares/conditions', condition),
  
  // Create station count condition
  createStationCountCondition: (data: { name: string; description?: string; minStations?: number; maxStations?: number }): Promise<AxiosResponse<FareCondition>> =>
    api.post('/fares/conditions/station-count', data),
  
  // Get condition by ID
  getConditionById: (id: string): Promise<AxiosResponse<FareCondition>> =>
    api.get(`/fares/conditions/${id}`),
  
  // Get all conditions
  getAllConditions: (): Promise<AxiosResponse<FareCondition[]>> =>
    api.get('/fares/conditions'),
  
  // Get active conditions
  getActiveConditions: (): Promise<AxiosResponse<FareCondition[]>> =>
    api.get('/fares/conditions/active'),
  
  // Get conditions by type
  getConditionsByType: (conditionType: string): Promise<AxiosResponse<FareCondition[]>> =>
    api.get(`/fares/conditions/type/${conditionType}`),
  
  // Update condition
  updateCondition: (id: string, condition: Partial<FareCondition>): Promise<AxiosResponse<FareCondition>> =>
    api.put(`/fares/conditions/${id}`, condition),
  
  // Delete condition
  deleteCondition: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/fares/conditions/${id}`),
};

// Feature Flags API
export const featureAPI = {
  // Create or update feature
  saveFeature: (feature: Partial<AppFeature>): Promise<AxiosResponse<AppFeature>> =>
    api.post('/features', feature),
  
  // Get feature by code
  getFeatureByCode: (code: string): Promise<AxiosResponse<AppFeature>> =>
    api.get(`/features/code/${code}`),
  
  // Get feature by name
  getFeatureByName: (featureName: string): Promise<AxiosResponse<AppFeature>> =>
    api.get(`/features/name/${featureName}`),
  
  // Check if feature is enabled by code
  isFeatureEnabledByCode: (code: string, cityId?: string | null): Promise<AxiosResponse<boolean>> =>
    api.get(`/features/code/${code}/enabled`, { params: { cityId } }),
  
  // Set global enabled status
  setGlobalEnabled: (code: string, enabled: boolean): Promise<AxiosResponse<AppFeature>> =>
    api.put(`/features/code/${code}/global-enabled`, null, { params: { enabled } }),
  
  // Set city-specific override
  setCityOverride: (code: string, cityId: string, enabled: boolean): Promise<AxiosResponse<AppFeature>> =>
    api.put(`/features/code/${code}/city-override`, null, { params: { cityId, enabled } }),
  
  // Remove city-specific override
  removeCityOverride: (code: string, cityId: string): Promise<AxiosResponse<AppFeature>> =>
    api.delete(`/features/code/${code}/city-override`, { params: { cityId } }),
  
  // Get all features
  getAllFeatures: (): Promise<AxiosResponse<AppFeature[]>> =>
    api.get('/features'),
  
  // Delete feature
  deleteFeature: (code: string): Promise<AxiosResponse<void>> =>
    api.delete(`/features/code/${code}`),
};

export default api; 